import PropTypes from "prop-types";
import * as React from "react";
import { Button, Link, Typography } from "../themeV2";
import css from "./ErrorFallback.module.css";

const showsDetails = import.meta.env.VITE_ENV !== "production";

const ErrorDetails = ({ error }) =>
  showsDetails ? (
    <Typography variant="body-sm" color="error">
      {String(error?.message ?? error)}
    </Typography>
  ) : null;

ErrorDetails.propTypes = { error: PropTypes.any };

const PageFallback = ({ error }) => {
  const headingRef = React.useRef(null);

  React.useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className={css.page}>
      <div className={css.pageContent}>
        <Typography
          ref={headingRef}
          variant="headline-md"
          as="h1"
          tabIndex={-1}
          className={css.heading}
        >
          Something Went Wrong
        </Typography>
        <Typography color="variant">
          Sorry, this page hit a problem. Reloading usually fixes it.
        </Typography>
        <ErrorDetails error={error} />
        <div className={css.actions}>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
          <Button variant="outlined" as="a" href="/">
            Go to homepage
          </Button>
        </div>
        <Link href="mailto:support@streamily.com">Contact support</Link>
      </div>
    </div>
  );
};

PageFallback.propTypes = { error: PropTypes.any };

const InlineFallback = ({ error, resetErrorBoundary }) => (
  <div role="alert" className={css.inline}>
    <Typography variant="body-md">
      Sorry, this section hit a problem.
    </Typography>
    <ErrorDetails error={error} />
    <Button variant="tonal" onClick={resetErrorBoundary}>
      Try again
    </Button>
  </div>
);

InlineFallback.propTypes = {
  error: PropTypes.any,
  resetErrorBoundary: PropTypes.func.isRequired,
};

export default function ErrorFallback({ variant, ...props }) {
  return variant === "inline" ? (
    <InlineFallback {...props} />
  ) : (
    <PageFallback {...props} />
  );
}

ErrorFallback.propTypes = {
  variant: PropTypes.oneOf(["page", "inline"]).isRequired,
  error: PropTypes.any,
  resetErrorBoundary: PropTypes.func.isRequired,
};
